import React from 'react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import {
	Checkbox,
	MessageBar,
	MessageBarType,
	ProgressIndicator,
	Stack,
	StackItem,
} from '@fluentui/react';
import { IDragDroppedFile, IOutlookAppCommands, IOutlookItemInfo } from '../state/CorporateArchiveStates/LegalEntityDocumentsStateOutlook';
import LegalEntityDocument from './LegalEntityDocument';
import {
	EntityAPI,
	IEmailFilingRequest,
	IEntityTypeUserInfo,
	IFilingRequestBase,
	IFilingResult,
	IUserConfigPackage,
} from '../API';

export interface IDocumentUploadProps {
	state: DocumentUploadState;
}

class DocumentUpload extends React.Component<IDocumentUploadProps, {}> {
	componentDidMount(): void { }

	render(): React.ReactNode {
		return (
			<Stack>
				<Stack horizontal>
					<div style={{ marginRight: 10 }}>
						{' '}
						{LegalEntityDocument.getDocumentIcon(this.props.state.itemName || '')}
					</div>

					<Checkbox
						label={this.props.state.itemName}
						defaultChecked={this.props.state.selected}
						onChange={(_, checked) => {
							this.props.state.setSelected(!!checked);
						}}
					/>
				</Stack>

				{this.props.state.uploadProgressPercent > 0 && (
					<Stack horizontal>
						<StackItem grow>
							<ProgressIndicator
								percentComplete={this.props.state.uploadProgressPercent}
							/>
						</StackItem>
						<StackItem>
							<span>{this.props.state.uploadProgressPercent}%</span>
						</StackItem>
					</Stack>
				)}

				{this.props.state.error ? (
					<MessageBar messageBarType={MessageBarType.error}>
						{this.props.state.error}
					</MessageBar>
				) : (
					<></>
				)}
			</Stack>
		);
	}
}

export default observer(DocumentUpload);

export class DocumentUploadState {
	private commands: IOutlookAppCommands;
	public userConfig: IUserConfigPackage;
	public attachment?: Office.AttachmentDetails;

	public selected: boolean = true;

	public uploadProgressPercent: number = 0;
	public entityInfo?: IEntityTypeUserInfo;

	public uploadAndCreateNewEntity: boolean = false;

	public itemName?: string;

	private url: string;
	private entityId: string;
	message?: IOutlookItemInfo;
	file?: IDragDroppedFile;

	constructor(
		entityType: IEntityTypeUserInfo | undefined,
		entityId: string,
		url: string,
		userConfig: IUserConfigPackage,
		commands: IOutlookAppCommands
	) {
		this.userConfig = userConfig;
		this.commands = commands;
		this.entityInfo = entityType;
		this.url = url;
		this.entityId = entityId;

		makeAutoObservable(this);
	}

	public setSelectedItemFromFile(file: IDragDroppedFile) {
		this.attachment = undefined;
		this.message = undefined;
		this.file = file;
		this.itemName = file.name;
	}

	public setSelectedItemFromMessage(message?: IOutlookItemInfo, attachment?: Office.AttachmentDetails) {
		this.message = message;
		this.attachment = attachment;
		this.itemName = attachment?.name || (message?.subject + '.eml');
	}

	public setItemName(itemName: string) {
		this.itemName = itemName;
	}

	public setSelected(selected: boolean) {
		this.selected = selected;
	}

	public setUploadProgress(percent: number) {
		this.uploadProgressPercent = percent;
	}

	public error: string = '';
	public setError(error: string) {
		this.error = error;
	}

	public setEntityId(id: string) {
		this.entityId = id;
	}

	public async upload(allowCreateNewEntity: boolean): Promise<IFilingResult> {
		this.setUploadProgress(0);
		let api = EntityAPI.fromEntityTypeId(
			this.entityInfo?.id || '',
			await this.userConfig.getToken(),
		);
		let result: IFilingResult = { location: '', error: '' };

		let uploadData: IFilingRequestBase = {
			emailId: this.message?.id || '',
			conversationId: this.message?.conversationId || '',
			internetMessageId: this.message?.internetMessageId || '',
			url: this.url,
			entityId: this.entityId,
			entityType: this.entityInfo?.id || '',
			createNewEntity: allowCreateNewEntity,
			senderEmail: Office.context.mailbox.userProfile.emailAddress
		};

		//Get the file
		if (this.attachment || this.file) {

			let file: File | undefined;

			if (this.file) {
				//Contruct the file from the Blob fileContent
				file = new File([this.file.fileContent], this.file.name);
			}

			if (!file && this.attachment) {

				let attachmentContents = await this.commands.getAttachmentContent(
					this.attachment.id,
				);
				if (attachmentContents) {
					var blob = this.b64toBlob(attachmentContents.content);
					file = new File([blob], this.attachment.name);
				}
			}

			if (file) {
				result = await api.saveAttachment(file, uploadData, (progress) => {
					this.setUploadProgress(progress);
				});
			}

		} else {
			let emailUpload = uploadData as IEmailFilingRequest;
			result = await api.saveEmail(emailUpload);
		}

		if (result.error) {
			this.setError(result.error);
		}

		return result;
	}

	private b64toBlob(b64Data: string, contentType = '', sliceSize = 512): Blob {
		const byteCharacters = atob(b64Data);
		const byteArrays = [];

		for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
			const slice = byteCharacters.slice(offset, offset + sliceSize);

			const byteNumbers = new Array(slice.length);
			for (let i = 0; i < slice.length; i++) {
				byteNumbers[i] = slice.charCodeAt(i);
			}

			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}

		const blob = new Blob(byteArrays, { type: contentType });
		return blob;
	}
}
